import { base58_to_binary, binary_to_base58} from 'base58-js'
import { sha256 } from 'js-sha256'
import {Buffer} from 'buffer'
import Web3 from 'web3'
import { tronRestFetch } from './tronApi.mjs'

function invalidAddress(address){
  if(/^0x[0-9a-fA-F]{40}/.test(address) != true) throw 'invalid address'
}
export const base58en = hex => {
  invalidAddress(hex)
  hex = hex.replace('0x', '41')
  const checksum = checksumAddr(hex)
  const res = binary_to_base58(Buffer.from(hex + checksum, 'hex'))
  if(res.substr(0, 1) != 'T')
    throw 'invalid address'
  return res
}
export const base58de = addr => {
  const hex = Buffer.from(base58_to_binary(addr)).toString('hex')
  let address = hex.substr(0, hex.length - 8)
  const checksum = checksumAddr(address)
  if(checksum != hex.substr(hex.length - 8))
    throw 'invalid tron address'
  if(address.substr(0, 2) != '41')
    throw 'invalid tron address'
  address = '0x' + address.substr(2)
  invalidAddress(address)
  return address
}

/** UI 展示用：hex / base58 / tronWeb.defaultAddress 统一为 T 开头地址 */
export function resolveTronDisplayAddress(account) {
  const raw = String(account || '').trim()
  if (raw.startsWith('T') && raw.length >= 34) return raw
  if (raw) {
    try {
      let hex = raw
      if (hex.startsWith('41') && hex.length === 42) hex = `0x${hex.slice(2)}`
      else if (!hex.startsWith('0x')) hex = `0x${hex}`
      if (/^0x[0-9a-fA-F]{40}$/.test(hex)) return base58en(hex)
    } catch (_e) {
      void _e
    }
  }
  const tw = resolveTronWebInstance()
  const b58 = tw?.defaultAddress?.base58
  if (b58 && String(b58).startsWith('T')) return String(b58)
  return raw
}
function checksumAddr(hex){
  return sha256(
    Buffer.from(
      sha256(
        Buffer.from(hex, 'hex')
      ),
      'hex'
    )
  ).substr(0, 8)
}

export function toEvmAddress(account) {
  const s = String(account || '').trim()
  if (!s) return ''
  if (s.startsWith('T')) return base58de(s)
  return s.startsWith('0x') ? s : `0x${s}`
}

export function tronOwnerHex(account) {
  const evm = toEvmAddress(account)
  if (!evm) return '410000000000000000000000000000000000000000'
  return evm.replace(/^0x/i, '41')
}

export function encodeTronAbiParams(types, values) {
  const web3 = new Web3()
  const encoded = values.map((v, i) => (types[i] === 'address' ? toEvmAddress(v) : v))
  return web3.eth.abi.encodeParameters(types, encoded).replace(/^0x/, '')
}

function parseTronConstantUint(hex) {
  const raw = String(hex || '').trim()
  if (!raw) return '0'
  const slice = raw.length > 64 ? raw.slice(-64) : raw
  return BigInt(`0x${slice}`).toString()
}

function toTronHexAddress(addr) {
  if (typeof addr !== 'string') return addr
  if (addr.startsWith('T')) {
    const hex = Buffer.from(base58_to_binary(addr)).toString('hex')
    return hex.substr(0, hex.length - 8)
  }
  if (addr.startsWith('0x')) return `41${addr.slice(2)}`
  if (addr.startsWith('41') && addr.length === 42) return addr
  return addr
}

export function resolveTronWebInstance() {
  if (typeof window === 'undefined') return null
  return window.tronWeb || window.tronLink?.tronWeb || null
}

export function isTronWebProvider(provider) {
  if (!provider) return false
  if (typeof provider.contract === 'function' && typeof provider.trx?.getBalance === 'function') return true
  if (provider === resolveTronWebInstance()) return true
  return false
}

export function normalizeTronTxResult(res) {
  if (!res) return { transactionHash: '' }
  if (typeof res === 'string') {
    const h = res.startsWith('0x') ? res : `0x${res}`
    return { transactionHash: h }
  }
  const txid =
    res?.transaction?.txID ||
    res?.txid ||
    res?.transactionHash ||
    res?.txID ||
    res?.signedTransaction?.txID ||
    ''
  if (!txid) return { transactionHash: '' }
  return { transactionHash: txid.startsWith('0x') ? txid : `0x${txid}` }
}

export function requireTronWeb() {
  const tw = resolveTronWebInstance()
  if (!tw || typeof tw.contract !== 'function') {
    const err = new Error('NO_TRON_WALLET')
    err.code = 'NO_PROVIDER'
    throw err
  }
  if (typeof window !== 'undefined' && !window.tronWeb) {
    window.tronWeb = tw
  }
  return tw
}

export async function readTronContractView({
  contractHex,
  accountHex,
  fn,
  types = [],
  values = [],
  tronWeb = null,
  skipInjected = false,
}) {
  const ownerHex = tronOwnerHex(accountHex)
  const tw = skipInjected ? null : (tronWeb || resolveTronWebInstance())
  const contractBase58 = base58en(contractHex)
  const fnName = String(fn || '').split('(')[0]

  if (tw?.contract) {
    try {
      const { default: Erc20Abi } = await import('./abis/IERC20.js')
      const contract = await tw.contract(JSON.parse(JSON.stringify(Erc20Abi)), contractBase58)
      const fnCall = contract[fnName]
      if (!fnCall) throw new Error('TRON_FN_MISSING')
      const args = values.map((v, i) => (types[i] === 'address' ? base58en(toEvmAddress(v)) : v))
      const result = await fnCall(...args).call()
      return String(result?.toString?.() ?? result ?? '0')
    } catch (_e) {
      if (!skipInjected) throw _e
    }
  }

  const selector = types.length ? `${fnName}(${types.join(',')})` : `${fnName}()`
  const parameter = types.length ? encodeTronAbiParams(types, values) : ''
  const json = await triggerConstantContract(contractBase58, selector, parameter, ownerHex)
  if (!json?.constant_result?.[0]) throw new Error('TRON_READ_FAILED')
  return parseTronConstantUint(json.constant_result[0])
}

export async function readTronNativeBalance(accountHex, tronWeb = null) {
  const ownerBase58 = base58en(toEvmAddress(accountHex))
  const tw = tronWeb || resolveTronWebInstance()
  if (tw?.trx?.getBalance) {
    const sun = await tw.trx.getBalance(ownerBase58)
    return String(sun ?? '0')
  }
  const res = await tronRestFetch('wallet/getaccount', {
    address: ownerBase58,
    visible: true,
  })
  return String(res?.balance ?? '0')
}

export function normalizeTronTxId(txHash) {
  return String(txHash || '').trim().replace(/^0x/i, '')
}

async function fetchTronTransactionInfo(txId, endpoint) {
  return tronRestFetch(endpoint, { value: txId }).catch(() => null)
}

/** Tron 回执：优先 wallet（含未固化块），再 fallback walletsolidity */
export async function getTronTransactionReceipt(txHash) {
  const id = normalizeTronTxId(txHash)
  if (!id) return null
  let res = await fetchTronTransactionInfo(id, 'wallet/gettransactioninfobyid')
  if (!res?.id) {
    res = await fetchTronTransactionInfo(id, 'walletsolidity/gettransactioninfobyid')
  }
  if (!res?.id) return null
  const result = res.receipt?.result
  if (result === 'REVERT') {
    return { status: false, blockNumber: res.blockNumber, transactionHash: `0x${id}` }
  }
  if (result === 'SUCCESS' || res.blockNumber) {
    return { status: true, blockNumber: res.blockNumber, transactionHash: `0x${id}` }
  }
  return null
}

export const triggerConstantContract = (contract, method, args, ownerHex = null) => {
  return tronRestFetch('walletsolidity/triggerconstantcontract', {
    owner_address: ownerHex || '410000000000000000000000000000000000000000',
    contract_address: toTronHexAddress(contract),
    function_selector: method,
    parameter: args,
  })
}

export const triggerconstantcontract = triggerConstantContract

if (typeof window !== 'undefined') {
  window.base58de = base58de
  window.base58en = base58en
}
